import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, Mail, Search } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    {
      title: 'Ordering & Payment',
      questions: [
        {
          q: 'How do I place an order?',
          a: 'Browse our collection, add items to your cart, and proceed to checkout. Fill in your delivery information and complete your order. You\'ll receive a confirmation email with your order details.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We currently accept Cash on Delivery (COD). You can pay with cash or card when your furniture is delivered to your home.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes! Since we use Cash on Delivery, you don\'t need to share payment information online. You pay only when your furniture arrives.'
        },
        {
          q: 'Can I cancel or modify my order?',
          a: 'Yes, you can cancel or modify your order before it ships. Contact us at info@skyfurniture.ca or call (123) 456-7890 as soon as possible.'
        },
      ]
    },
    {
      title: 'Shipping & Delivery',
      questions: [
        {
          q: 'How long does delivery take?',
          a: 'Standard delivery is 3-5 business days from order confirmation. You\'ll receive a tracking email with estimated delivery date.'
        },
        {
          q: 'Do you deliver to my area?',
          a: 'We deliver to 116+ cities across Ontario. Check our Store Locations page or enter your postal code at checkout to see if we deliver to your area.'
        },
        {
          q: 'What is white glove delivery?',
          a: 'White glove service includes professional delivery, furniture placement in your chosen room, assembly if needed, and removal of all packaging materials.'
        },
        {
          q: 'Can I choose a delivery date?',
          a: 'Yes! During checkout, you can select a preferred delivery date. Our team will call you 24 hours before to confirm the time window.'
        },
        {
          q: 'What if I\'m not home during delivery?',
          a: 'Someone 18+ must be home to receive the delivery. If you miss it, we\'ll reschedule for another day. Additional delivery fees may apply.'
        },
      ]
    },
    {
      title: 'Returns & Exchanges',
      questions: [
        {
          q: 'What is your return policy?',
          a: '30-day return policy on eligible items. Items must be unused, in original condition with all packaging. A 15% restocking fee applies. See our Returns & Exchanges page for full details.'
        },
        {
          q: 'How do I return an item?',
          a: 'Contact us at returns@skyfurniture.ca within 30 days. We\'ll review your request, arrange pickup, and process your refund after inspection.'
        },
        {
          q: 'Can I exchange my furniture?',
          a: 'Yes! Exchange for items of equal value within 30 days. No restocking fee for exchanges. We\'ll pickup the old item and deliver the new one.'
        },
        {
          q: 'What if my furniture arrives damaged?',
          a: 'Contact us within 48 hours with photos. We\'ll arrange a free replacement or full refund with no fees. Defective items are our responsibility.'
        },
      ]
    },
    {
      title: 'Products & Availability',
      questions: [
        {
          q: 'Are items shown in stock?',
          a: 'Yes, items displayed on our website are in stock and ready to ship. Out of stock items show "Notify Me" button instead of "Add to Cart".'
        },
        {
          q: 'Do you offer custom furniture?',
          a: 'Currently, we offer our curated collection only. Custom orders are not available at this time. Contact us if you have specific requirements.'
        },
        {
          q: 'Can I see furniture in person before buying?',
          a: 'We operate online-first to offer better prices. However, we provide detailed photos, dimensions, and descriptions. Our return policy ensures satisfaction.'
        },
        {
          q: 'Do you offer assembly service?',
          a: 'Yes! Assembly is included with white glove delivery at no extra cost. Our team will assemble your furniture during delivery.'
        },
      ]
    },
    {
      title: 'Warranty & Care',
      questions: [
        {
          q: 'What warranty do you offer?',
          a: '1-year manufacturer warranty covering defects in materials and workmanship. Does not cover normal wear and tear or misuse. See Warranty Information page.'
        },
        {
          q: 'How do I care for my furniture?',
          a: 'Regular dusting, avoid direct sunlight, use coasters for drinks, and clean spills immediately. Specific care instructions are included with your delivery.'
        },
        {
          q: 'What if I need replacement parts?',
          a: 'Contact us with your order number and photos. We\'ll send replacement parts free of charge if covered under warranty.'
        },
      ]
    },
  ];

  const allQuestions = categories.flatMap((cat, catIndex) =>
    cat.questions.map((q, qIndex) => ({
      ...q,
      category: cat.title,
      index: `${catIndex}-${qIndex}`,
    }))
  );

  const filteredQuestions = searchTerm
    ? allQuestions.filter(
        q =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allQuestions;

  const FAQItem = ({ question, answer, index }) => {
    const isOpen = openIndex === index;

    return (
      <div className="border-b border-dark-200 last:border-b-0">
        <button
          onClick={() => setOpenIndex(isOpen ? null : index)}
          className="w-full py-6 px-4 flex items-start justify-between gap-4 hover:bg-dark-50 transition-smooth text-left"
        >
          <span className="font-body font-semibold text-dark-900 flex-1">
            {question}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-primary-600 flex-shrink-0 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <p className="px-4 pb-6 text-dark-600 font-body leading-relaxed">{answer}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">FAQ</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <HelpCircle className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              Find answers to common questions about ordering, delivery, and returns
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Search */}
        <div className="mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-dark-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-4 py-4 border-2 border-dark-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-smooth font-body text-lg"
            />
          </div>
        </div>

        {/* Questions */}
        {searchTerm ? (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden mb-12">
            <div className="p-6 border-b border-dark-200 bg-dark-50">
              <h2 className="text-2xl font-display font-bold text-dark-900">
                Search Results ({filteredQuestions.length})
              </h2>
            </div>
            {filteredQuestions.length > 0 ? (
              filteredQuestions.map(q => (
                <FAQItem key={q.index} question={q.q} answer={q.a} index={q.index} />
              ))
            ) : (
              <div className="p-12 text-center">
                <p className="text-dark-500 font-body">No questions found matching your search.</p>
              </div>
            )}
          </div>
        ) : (
          categories.map((category, catIndex) => (
            <div key={catIndex} className="mb-8 animate-slide-up" style={{ animationDelay: `${catIndex * 0.1}s` }}>
              <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                <div className="p-6 border-b border-dark-200 bg-gradient-to-r from-primary-50 to-accent-50">
                  <h2 className="text-2xl font-display font-bold text-dark-900">
                    {category.title}
                  </h2>
                </div>
                {category.questions.map((q, qIndex) => (
                  <FAQItem
                    key={`${catIndex}-${qIndex}`}
                    question={q.q}
                    answer={q.a}
                    index={`${catIndex}-${qIndex}`}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Still Have Questions */}
        <div className="bg-gradient-accent text-white rounded-2xl shadow-colored p-8 md:p-12 text-center">
          <h2 className="text-3xl font-display font-bold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-accent-100 font-body mb-8">
            Our customer service team is here to help!
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-accent-700 rounded-xl font-body font-semibold text-lg hover:bg-dark-50 transition-smooth"
          >
            <Mail className="w-6 h-6" />
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;